/**
 * Static referential for the Weet AI generation options: avatars, voices and
 * backgrounds. Mirrors the source of truth in speacher_front's
 * `aiavatarGenerationModel.ts` so speach-front can render an option picker
 * before delegating to the Weet editor.
 *
 * Keep this file aligned with speacher_front — any avatar/voice/background
 * added there must be reflected here (and vice versa).
 */

import {AIAvatar} from "@/models/ai/enum/aiAvatar.enum";
import {AIAvatarStyle} from "@/models/ai/enum/aiAvatarStyle.enum";
import {AIAvatarGender} from "@/models/ai/enum/aiAvatarGender.enum";
import {AIBackground} from "@/models/ai/enum/aiBackground.enum";
import {AIVoice} from "@/models/ai/enum/aiVoice.enum";

export interface AIAvatarEntry {
    avatar: AIAvatar;
    label: string;
    style: AIAvatarStyle;
    gender: AIAvatarGender;
    version: number;
    freemium: boolean;
    realistic: boolean;
    custom: boolean;
}

export interface AIBackgroundEntry {
    background: AIBackground;
    label: string;
    url: string;
    freemium: boolean;
}

export type AIVoiceProvider = 'MICROSOFT' | 'GEMINI';

export interface AIVoiceEntry {
    voice: AIVoice;
    label: string;
    /** Azure locale (e.g. "en-US", "fr-FR"). Empty for legacy short-form voices. */
    lang: string;
    gender: AIAvatarGender;
    /** TTS provider. Defaults to MICROSOFT when omitted. */
    provider?: AIVoiceProvider;
    /** Advanced (paid) voice — Gemini today. Hidden unless ENABLE_ADVANCED_TTS. Defaults to false. */
    advanced?: boolean;
}

/**
 * Avatar thumbnails are NOT bundled with speach-front — they are read directly
 * from speacher-front at picker-render time (via whatever URL / require the
 * picker component chooses). The referential only exposes the identifier +
 * metadata here.
 */

/** Backgrounds are hosted on GCS (weet_prod_data/avatar_background/). */
const BG_BASE = "https://storage.googleapis.com/weet_prod_data/avatar_background/";

/**
 * Available avatars. Ordered alphabetically by label.
 */
export const LIST_OF_AVATAR: AIAvatarEntry[] = [
    {avatar: AIAvatar.ABIGAIL,  label: "Abigail",  style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.ANDY,     label: "Andy",     style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: false, custom: true},
    {avatar: AIAvatar.ARABELLA, label: "Arabella", style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: false, custom: false},
    {avatar: AIAvatar.ARTHUR,   label: "Arthur",   style: AIAvatarStyle.STATIC, gender: AIAvatarGender.MALE,   version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.DAN,      label: "Dan",      style: AIAvatarStyle.STATIC, gender: AIAvatarGender.MALE,   version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.DANA,     label: "Dana",     style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: false, custom: true},
    {avatar: AIAvatar.DARNEL,   label: "Darnel",   style: AIAvatarStyle.STATIC, gender: AIAvatarGender.MALE,   version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.ELIA,     label: "Elia",     style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.EVA,      label: "Eva",      style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.FAIZA,    label: "Faiza",    style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: false, custom: false},
    {avatar: AIAvatar.HANNAH,   label: "Hannah",   style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.ISABELLA, label: "Isabella", style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.JAD,      label: "Jad",      style: AIAvatarStyle.STATIC, gender: AIAvatarGender.MALE,   version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.JEN,      label: "Jen",      style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.JESSICA,  label: "Jessica",  style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.JOHN,     label: "John",     style: AIAvatarStyle.STATIC, gender: AIAvatarGender.MALE,   version: 2, freemium: true, realistic: false, custom: false},
    {avatar: AIAvatar.LARA,     label: "Lara",     style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: false, custom: false},
    {avatar: AIAvatar.LIAM,     label: "Liam",     style: AIAvatarStyle.STATIC, gender: AIAvatarGender.MALE,   version: 2, freemium: true, realistic: false, custom: false},
    {avatar: AIAvatar.MARC,     label: "Marc",     style: AIAvatarStyle.STATIC, gender: AIAvatarGender.MALE,   version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.MARIE,    label: "Marie",    style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.MARTHA,   label: "Martha",   style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.MICHELLE, label: "Michelle", style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.MIKE,     label: "Mike",     style: AIAvatarStyle.STATIC, gender: AIAvatarGender.MALE,   version: 2, freemium: true, realistic: false, custom: false},
    {avatar: AIAvatar.MYA,      label: "Mya",      style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.OLIVIA,   label: "Olivia",   style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.ROSIE,    label: "Rosie",    style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: false, custom: false},
    {avatar: AIAvatar.RUBY,     label: "Ruby",     style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.RYAN,     label: "Ryan",     style: AIAvatarStyle.STATIC, gender: AIAvatarGender.MALE,   version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.SARAH,    label: "Sarah",    style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.STEEVE,   label: "Steeve",   style: AIAvatarStyle.STATIC, gender: AIAvatarGender.MALE,   version: 2, freemium: true, realistic: true,  custom: false},
    {avatar: AIAvatar.STELLA,   label: "Stella",   style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: false, custom: false},
    {avatar: AIAvatar.TALIA,    label: "Talia",    style: AIAvatarStyle.STATIC, gender: AIAvatarGender.FEMALE, version: 2, freemium: true, realistic: false, custom: false},
];

/**
 * Available backgrounds.
 */
export const LIST_OF_BACKGROUND: AIBackgroundEntry[] = [
    {background: AIBackground.WALL_1,       label: "Wall 1",       url: BG_BASE + "wall1.jpeg",       freemium: true},
    {background: AIBackground.ROOM_1,       label: "Room 1",       url: BG_BASE + "room1.jpeg",       freemium: true},
    {background: AIBackground.ROOM_2,       label: "Room 2",       url: BG_BASE + "room2.jpeg",       freemium: true},
    {background: AIBackground.ROOM_3,       label: "Room 3",       url: BG_BASE + "room3.jpeg",       freemium: true},
    {background: AIBackground.ROOM_4,       label: "Room 4",       url: BG_BASE + "room4.jpeg",       freemium: true},
    {background: AIBackground.INDUSTRIAL_1, label: "Industrial 1", url: BG_BASE + "industrial1.jpeg", freemium: true},
    {background: AIBackground.INDUSTRIAL_2, label: "Industrial 2", url: BG_BASE + "industrial2.jpeg", freemium: true},
    {background: AIBackground.INDUSTRIAL_3, label: "Industrial 3", url: BG_BASE + "industrial3.jpeg", freemium: true},
    {background: AIBackground.WAREHOUSE_1,  label: "Warehouse 1",  url: BG_BASE + "warehouse1.jpeg",  freemium: true},
    {background: AIBackground.OFFICE_1,     label: "Office 1",     url: BG_BASE + "office1.jpeg",     freemium: true},
    {background: AIBackground.OFFICE_2,     label: "Office 2",     url: BG_BASE + "office2.jpeg",     freemium: true},
    {background: AIBackground.OFFICE_3,     label: "Office 3",     url: BG_BASE + "office3.jpeg",     freemium: true},
    {background: AIBackground.OFFICE_4,     label: "Office 4",     url: BG_BASE + "office4.jpeg",     freemium: true},
    {background: AIBackground.OFFICE_5,     label: "Office 5",     url: BG_BASE + "office5.jpeg",     freemium: true},
    {background: AIBackground.OFFICE_6,     label: "Office 6",     url: BG_BASE + "office6.jpeg",     freemium: true},
    {background: AIBackground.OFFICE_7,     label: "Office 7",     url: BG_BASE + "office7.jpeg",     freemium: true},
    {background: AIBackground.OFFICE_8,     label: "Office 8",     url: BG_BASE + "office8.jpeg",     freemium: true},
    {background: AIBackground.OFFICE_9,     label: "Office 9",     url: BG_BASE + "office9.jpeg",     freemium: true},
    {background: AIBackground.OFFICE_10,    label: "Office 10",    url: BG_BASE + "office10.jpeg",    freemium: true},
    {background: AIBackground.OFFICE_11,    label: "Office 11",    url: BG_BASE + "office11.jpeg",    freemium: true},
    {background: AIBackground.OFFICE_12,    label: "Office 12",    url: BG_BASE + "office12.jpeg",    freemium: true},
    {background: AIBackground.OFFICE_13,    label: "Office 13",    url: BG_BASE + "office13.jpeg",    freemium: true},
    {background: AIBackground.WHITE,        label: "White",        url: BG_BASE + "white.jpg",        freemium: true},
    {background: AIBackground.ORANGE,       label: "Orange",       url: BG_BASE + "oragne.jpg",       freemium: true},
    {background: AIBackground.YELLOW,       label: "Yellow",       url: BG_BASE + "yellow.jpg",       freemium: true},
    {background: AIBackground.BLACK,        label: "Black",        url: BG_BASE + "black.jpg",        freemium: true},
    {background: AIBackground.RED,          label: "Red",          url: BG_BASE + "red.jpg",          freemium: true},
    {background: AIBackground.BLUE,         label: "Blue",         url: BG_BASE + "blue.jpg",         freemium: true},
    {background: AIBackground.GREEN,        label: "Green",        url: BG_BASE + "green.jpg",        freemium: true},
    {background: AIBackground.PURPLE,       label: "Purple",       url: BG_BASE + "purple.jpg",       freemium: true},
];

/**
 * Available voices — this list MIRRORS speacher_front's `TTS_VOICES`
 * (`speacher_front/src/store/media/ttsCatalogModel.ts`): the 51 Microsoft
 * voices (advanced: false) then the 30 Gemini ones (advanced: true). ids,
 * labels and genders MUST match, since the Weet editor is what actually
 * displays and synthesizes them — a voice missing there cannot be rendered.
 * NB: `voiceUtil.ts`'s `listOfVoice` only holds the Microsoft subset.
 * Do NOT add language / gender in the label — the picker shows only the
 * short name.
 */
export const LIST_OF_VOICES: AIVoiceEntry[] = [
    // English (US)
    {voice: AIVoice.EN_US_ADAM,          label: "Adam",       lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_AMANDA,        label: "Amanda",     lang: "en-US", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_US_ANDREW,        label: "Andrew",     lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_AVA,           label: "Ava",        lang: "en-US", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_US_BRANDON,       label: "Brandon",    lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_BRIAN,         label: "Brian",      lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_CHRISTOPHER,   label: "Christopher",lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_CORA,          label: "Cora",       lang: "en-US", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_US_DAVIS,         label: "Davis",      lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_DEREK,         label: "Derek",      lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_DUSTIN,        label: "Dustin",     lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_EMMA,          label: "Emma",       lang: "en-US", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_US_EVELYN,        label: "Evelyn",     lang: "en-US", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_US_LEWIS,         label: "Lewis",      lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_LOLA,          label: "Lola",       lang: "en-US", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_US_NANCY,         label: "Nancy",      lang: "en-US", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_US_PHOEBE,        label: "Phoebe",     lang: "en-US", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_US_SAMUEL,        label: "Samuel",     lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_SERENA,        label: "Serena",     lang: "en-US", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_US_STEFFAN,       label: "Steffan",    lang: "en-US", gender: AIAvatarGender.MALE},
    // Turbo voices (short labels, no "Turbo" suffix)
    {voice: AIVoice.EN_US_ALLOY_TURBO,   label: "Alloy",      lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_ECHO_TURBO,    label: "Echo",       lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_FABLE_TURBO,   label: "Fable",      lang: "en-US", gender: AIAvatarGender.NEUTRAL},
    {voice: AIVoice.EN_US_NOVA_TURBO,    label: "Nova",       lang: "en-US", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_US_ONYX_TURBO,    label: "Onyx",       lang: "en-US", gender: AIAvatarGender.MALE},
    {voice: AIVoice.EN_US_SHIMMER_TURBO, label: "Shimmer",    lang: "en-US", gender: AIAvatarGender.FEMALE},

    // English (GB)
    {voice: AIVoice.EN_GB_ADA,           label: "Ada",        lang: "en-GB", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.EN_GB_OLLIE,         label: "Ollie",      lang: "en-GB", gender: AIAvatarGender.MALE},

    // German
    {voice: AIVoice.DE_DE_SERAPHINA,     label: "Seraphina",  lang: "de-DE", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.DE_DE_FLORIAN,       label: "Florian",    lang: "de-DE", gender: AIAvatarGender.MALE},

    // Spanish
    {voice: AIVoice.ES_ES_ARABELLA,      label: "Arabella",   lang: "es-ES", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.ES_ES_ISIDORA,       label: "Isidora",    lang: "es-ES", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.ES_ES_TRISTAN,       label: "Tristan",    lang: "es-ES", gender: AIAvatarGender.MALE},
    {voice: AIVoice.ES_ES_XIMENA,        label: "Ximena",     lang: "es-ES", gender: AIAvatarGender.FEMALE},

    // French
    {voice: AIVoice.FR_FR_LUCIEN,        label: "Lucien",     lang: "fr-FR", gender: AIAvatarGender.MALE},
    {voice: AIVoice.FR_FR_VIVIENNE,      label: "Vivienne",   lang: "fr-FR", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.FR_FR_REMY,          label: "Remy",       lang: "fr-FR", gender: AIAvatarGender.MALE},

    // Italian
    {voice: AIVoice.IT_IT_ALESSIO,       label: "Alessio",    lang: "it-IT", gender: AIAvatarGender.MALE},
    {voice: AIVoice.IT_IT_GIUSEPPE,      label: "Giuseppe",   lang: "it-IT", gender: AIAvatarGender.MALE},
    {voice: AIVoice.IT_IT_ISABELLA,      label: "Isabella",   lang: "it-IT", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.IT_IT_MARCELLO,      label: "Marcello",   lang: "it-IT", gender: AIAvatarGender.MALE},

    // Japanese / Korean / Portuguese
    {voice: AIVoice.JA_JP_MASARU,        label: "Masaru",     lang: "ja-JP", gender: AIAvatarGender.MALE},
    {voice: AIVoice.KO_KR_HYUNSU,        label: "Hyunsu",     lang: "ko-KR", gender: AIAvatarGender.MALE},
    {voice: AIVoice.PT_BR_MACERIO,       label: "Macerio",    lang: "pt-BR", gender: AIAvatarGender.MALE},
    {voice: AIVoice.PT_BR_THALITA,       label: "Thalita",    lang: "pt-BR", gender: AIAvatarGender.FEMALE},

    // Chinese (Mandarin)
    {voice: AIVoice.ZH_CN_XIAOXIAO,      label: "Xiaoxiao",   lang: "zh-CN", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.ZH_CN_XIAOCHEN,      label: "Xiaochen",   lang: "zh-CN", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.ZH_CN_XIAOYU,        label: "Xiaoyu",     lang: "zh-CN", gender: AIAvatarGender.FEMALE},
    {voice: AIVoice.ZH_CN_YUNYI,         label: "Yunyi",      lang: "zh-CN", gender: AIAvatarGender.MALE},
    {voice: AIVoice.ZH_CN_YUNFAN,        label: "Yunfan",     lang: "zh-CN", gender: AIAvatarGender.MALE},
    {voice: AIVoice.ZH_CN_YUNXIAO,       label: "Yunxiao",    lang: "zh-CN", gender: AIAvatarGender.MALE},

    // ── Gemini voices (advanced TTS) — language-agnostic, shown only when ENABLE_ADVANCED_TTS is on ──
    {voice: AIVoice.GEMINI_ZEPHYR,       label: "Zephyr",       lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_PUCK,         label: "Puck",         lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_CHARON,       label: "Charon",       lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_KORE,         label: "Kore",         lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_FENRIR,       label: "Fenrir",       lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_LEDA,         label: "Leda",         lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_ORUS,         label: "Orus",         lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_AOEDE,        label: "Aoede",        lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_CALLIRRHOE,   label: "Callirrhoe",   lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_AUTONOE,      label: "Autonoe",      lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_ENCELADUS,    label: "Enceladus",    lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_IAPETUS,      label: "Iapetus",      lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_UMBRIEL,      label: "Umbriel",      lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_ALGIEBA,      label: "Algieba",      lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_DESPINA,      label: "Despina",      lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_ERINOME,      label: "Erinome",      lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_ALGENIB,      label: "Algenib",      lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_RASALGETHI,   label: "Rasalgethi",   lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_LAOMEDEIA,    label: "Laomedeia",    lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_ACHERNAR,     label: "Achernar",     lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_ALNILAM,      label: "Alnilam",      lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_SCHEDAR,      label: "Schedar",      lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_GACRUX,       label: "Gacrux",       lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_PULCHERRIMA,  label: "Pulcherrima",  lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_ACHIRD,       label: "Achird",       lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_ZUBENELGENUBI,label: "Zubenelgenubi",lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_VINDEMIATRIX, label: "Vindemiatrix", lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_SADACHBIA,    label: "Sadachbia",    lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_SADALTAGER,   label: "Sadaltager",   lang: "en-US", gender: AIAvatarGender.MALE,   provider: 'GEMINI', advanced: true},
    {voice: AIVoice.GEMINI_SULAFAT,      label: "Sulafat",      lang: "en-US", gender: AIAvatarGender.FEMALE, provider: 'GEMINI', advanced: true},
];

// Use-case tags per voice (the "vibe" of the voice) — mirrors speacher-front's TTS catalog.
// Keyed by the voice id (= the AIVoice value). Used by the Style step's voice filter.
export const VOICE_USE_CASES: Partial<Record<AIVoice, string[]>> = {
    [AIVoice.EN_US_ADAM]: ["Warm", "Expressive"],
    [AIVoice.EN_US_AMANDA]: ["Bright", "Expressive"],
    [AIVoice.EN_US_ANDREW]: ["Warm", "Expressive"],
    [AIVoice.EN_US_AVA]: ["Bright", "Expressive"],
    [AIVoice.EN_US_BRANDON]: ["Gravelly", "Expressive"],
    [AIVoice.EN_US_BRIAN]: ["Casual", "Expressive"],
    [AIVoice.EN_US_CHRISTOPHER]: ["Firm", "Professional"],
    [AIVoice.EN_US_CORA]: ["Mature", "Professional"],
    [AIVoice.EN_US_DAVIS]: ["Even", "Professional"],
    [AIVoice.EN_US_DEREK]: ["Friendly", "Expressive"],
    [AIVoice.EN_US_DUSTIN]: ["Youthful", "Expressive"],
    [AIVoice.EN_US_EMMA]: ["Upbeat", "Expressive"],
    [AIVoice.EN_US_EVELYN]: ["Clear", "Professional"],
    [AIVoice.EN_US_LEWIS]: ["Informative", "Professional"],
    [AIVoice.EN_US_LOLA]: ["Smooth", "Expressive"],
    [AIVoice.EN_US_NANCY]: ["Bright", "Expressive"],
    [AIVoice.EN_US_PHOEBE]: ["Soft", "Expressive"],
    [AIVoice.EN_US_SAMUEL]: ["Knowledgeable", "Professional"],
    [AIVoice.EN_US_SERENA]: ["Smooth", "Professional"],
    [AIVoice.EN_US_STEFFAN]: ["Firm", "Professional"],
    [AIVoice.EN_US_ALLOY_TURBO]: ["Even", "Professional"],
    [AIVoice.EN_US_ECHO_TURBO]: ["Smooth", "Professional"],
    [AIVoice.EN_US_FABLE_TURBO]: ["Lively", "Expressive"],
    [AIVoice.EN_US_NOVA_TURBO]: ["Bright", "Expressive"],
    [AIVoice.EN_US_ONYX_TURBO]: ["Firm", "Professional"],
    [AIVoice.EN_US_SHIMMER_TURBO]: ["Warm", "Professional"],
    [AIVoice.EN_GB_ADA]: ["Clear", "Professional"],
    [AIVoice.EN_GB_OLLIE]: ["Easy-going", "Expressive"],
    [AIVoice.DE_DE_SERAPHINA]: ["Bright", "Expressive"],
    [AIVoice.DE_DE_FLORIAN]: ["Informative", "Professional"],
    [AIVoice.ES_ES_ARABELLA]: ["Warm", "Expressive"],
    [AIVoice.ES_ES_ISIDORA]: ["Lively", "Expressive"],
    [AIVoice.ES_ES_TRISTAN]: ["Firm", "Professional"],
    [AIVoice.ES_ES_XIMENA]: ["Friendly", "Expressive"],
    [AIVoice.FR_FR_LUCIEN]: ["Clear", "Professional"],
    [AIVoice.FR_FR_VIVIENNE]: ["Warm", "Expressive"],
    [AIVoice.FR_FR_REMY]: ["Casual", "Expressive"],
    [AIVoice.IT_IT_ALESSIO]: ["Lively", "Expressive"],
    [AIVoice.IT_IT_GIUSEPPE]: ["Warm", "Expressive"],
    [AIVoice.IT_IT_ISABELLA]: ["Bright", "Expressive"],
    [AIVoice.IT_IT_MARCELLO]: ["Smooth", "Professional"],
    [AIVoice.JA_JP_MASARU]: ["Firm", "Professional"],
    [AIVoice.KO_KR_HYUNSU]: ["Friendly", "Expressive"],
    [AIVoice.PT_BR_MACERIO]: ["Warm", "Expressive"],
    [AIVoice.PT_BR_THALITA]: ["Bright", "Expressive"],
    [AIVoice.ZH_CN_XIAOXIAO]: ["Lively", "Expressive"],
    [AIVoice.ZH_CN_XIAOCHEN]: ["Casual", "Expressive"],
    [AIVoice.ZH_CN_XIAOYU]: ["Clear", "Professional"],
    [AIVoice.ZH_CN_YUNYI]: ["Informative", "Professional"],
    [AIVoice.ZH_CN_YUNFAN]: ["Clear", "Professional"],
    [AIVoice.ZH_CN_YUNXIAO]: ["Knowledgeable", "Professional"],
    [AIVoice.GEMINI_ZEPHYR]: ["Bright", "Expressive"],
    [AIVoice.GEMINI_PUCK]: ["Upbeat", "Expressive"],
    [AIVoice.GEMINI_CHARON]: ["Informative", "Professional"],
    [AIVoice.GEMINI_KORE]: ["Firm", "Professional"],
    [AIVoice.GEMINI_FENRIR]: ["Excitable", "Expressive"],
    [AIVoice.GEMINI_LEDA]: ["Youthful", "Expressive"],
    [AIVoice.GEMINI_ORUS]: ["Firm", "Professional"],
    [AIVoice.GEMINI_AOEDE]: ["Breezy", "Expressive"],
    [AIVoice.GEMINI_CALLIRRHOE]: ["Easy-going", "Expressive"],
    [AIVoice.GEMINI_AUTONOE]: ["Bright", "Expressive"],
    [AIVoice.GEMINI_ENCELADUS]: ["Breathy", "Professional"],
    [AIVoice.GEMINI_IAPETUS]: ["Clear", "Professional"],
    [AIVoice.GEMINI_UMBRIEL]: ["Easy-going", "Expressive"],
    [AIVoice.GEMINI_ALGIEBA]: ["Smooth", "Professional"],
    [AIVoice.GEMINI_DESPINA]: ["Smooth", "Professional"],
    [AIVoice.GEMINI_ERINOME]: ["Clear", "Professional"],
    [AIVoice.GEMINI_ALGENIB]: ["Gravelly", "Professional"],
    [AIVoice.GEMINI_RASALGETHI]: ["Informative", "Professional"],
    [AIVoice.GEMINI_LAOMEDEIA]: ["Upbeat", "Expressive"],
    [AIVoice.GEMINI_ACHERNAR]: ["Soft", "Professional"],
    [AIVoice.GEMINI_ALNILAM]: ["Firm", "Professional"],
    [AIVoice.GEMINI_SCHEDAR]: ["Even", "Professional"],
    [AIVoice.GEMINI_GACRUX]: ["Mature", "Expressive"],
    [AIVoice.GEMINI_PULCHERRIMA]: ["Forward", "Expressive"],
    [AIVoice.GEMINI_ACHIRD]: ["Friendly", "Expressive"],
    [AIVoice.GEMINI_ZUBENELGENUBI]: ["Casual", "Expressive"],
    [AIVoice.GEMINI_VINDEMIATRIX]: ["Gentle", "Expressive"],
    [AIVoice.GEMINI_SADACHBIA]: ["Lively", "Expressive"],
    [AIVoice.GEMINI_SADALTAGER]: ["Knowledgeable", "Professional"],
    [AIVoice.GEMINI_SULAFAT]: ["Warm", "Professional"],
};

/** Use-case tags of a voice (empty when unknown). */
export const getVoiceUseCases = (voice: AIVoice): string[] => VOICE_USE_CASES[voice] || [];

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Find the metadata entry for a given avatar. Returns undefined for legacy values. */
export const getAvatarEntry = (avatar: AIAvatar): AIAvatarEntry | undefined => {
    return LIST_OF_AVATAR.find(a => a.avatar === avatar);
};

/**
 * Parse the AI_AVATAR_CUSTOM conf (semicolon-separated allow-list, e.g. "ANDY;MORGAN")
 * into an upper-cased set of avatar names. Empty/undefined → empty list.
 */
export const parseCustomAvatarAllowList = (avatarCustom: string | null | undefined): string[] => {
    return avatarCustom ? avatarCustom.split(';').map(s => s.trim().toUpperCase()).filter(Boolean) : [];
};

/**
 * Whether an avatar entry is allowed given the AI_AVATAR_CUSTOM allow-list.
 * Mirrors speacher_front's AIAvatarSelectionPanel filter: avatars tagged `custom`
 * (Dana, Andy...) are only available when their name is in the allow-list.
 */
export const isCustomAvatarAllowed = (entry: AIAvatarEntry, avatarCustom: string | null | undefined): boolean => {
    if (!entry.custom) return true;
    return parseCustomAvatarAllowList(avatarCustom).includes(entry.avatar.toUpperCase());
};

/** LIST_OF_AVATAR filtered by the AI_AVATAR_CUSTOM allow-list (custom avatars gated by conf). */
export const getAvailableAvatars = (avatarCustom: string | null | undefined): AIAvatarEntry[] => {
    return LIST_OF_AVATAR.filter(a => isCustomAvatarAllowed(a, avatarCustom));
};

/**
 * Default avatar for a gender: the first available (allow-list aware) avatar of that gender,
 * falling back to the first available avatar when none matches. Used to keep the avatar coherent
 * with the selected voice's gender (voice = source of truth in the Style step).
 */
export const getDefaultAvatarForGender = (gender: AIAvatarGender, avatarCustom: string | null | undefined): AIAvatar | undefined => {
    const available = getAvailableAvatars(avatarCustom);
    return (available.find(a => a.gender === gender) ?? available[0])?.avatar;
};

/** Find the metadata entry for a given background. Returns undefined for legacy values. */
export const getBackgroundEntry = (background: AIBackground): AIBackgroundEntry | undefined => {
    return LIST_OF_BACKGROUND.find(b => b.background === background);
};

/** Find the metadata entry for a given voice. Returns undefined for legacy values. */
export const getVoiceEntry = (voice: AIVoice): AIVoiceEntry | undefined => {
    return LIST_OF_VOICES.find(v => v.voice === voice);
};

/**
 * All voices for a given locale (e.g. "fr-FR" → all French voices).
 * Match is case-insensitive on the language prefix ("en" → all en-* voices).
 */
export const getVoicesForLanguage = (langOrPrefix: string): AIVoiceEntry[] => {
    const needle = langOrPrefix.toLowerCase();
    return LIST_OF_VOICES.filter(v => v.lang.toLowerCase().startsWith(needle));
};

/** Distinct list of locales available across all voices. */
export const getAvailableVoiceLanguages = (): string[] => {
    return Array.from(new Set(LIST_OF_VOICES.map(v => v.lang))).sort();
};

/**
 * Static PNG thumbnail URL for an avatar. Served by speacher_front from its
 * public/ folder (files there aren't hashed by Vue CLI). File names use the
 * upper-case enum name: ABIGAIL.png, ARTHUR.png, …
 */
const AVATAR_THUMBNAIL_BASE = "/recorder_v2/AIAvatar/";
export const getAvatarThumbnailUrl = (avatar: AIAvatar): string => {
    return AVATAR_THUMBNAIL_BASE + avatar.toUpperCase() + ".png";
};

/**
 * Transparent-background webm animation URL for an avatar. Hosted on the same
 * GCS bucket as speacher_front consumes:
 *   `<AVATAR_ANIM_BUCKET>/<name lowercase>/<Name>_transparent.webm`
 */
const AVATAR_ANIM_BUCKET = "https://storage.googleapis.com/weet_avatar_2_2/";
export const getAvatarAnimUrl = (avatar: AIAvatar): string => {
    return AVATAR_ANIM_BUCKET + avatar.toLowerCase() + "/" + avatar + "_transparent.webm";
};
